import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { ArrowLeft, Loader, Sparkles } from 'lucide-react';
import { stripPiiMarkers } from '../../utils/copyFormatters';
import { ChatThread } from '../ask/ChatThread';
import { ChatInput } from '../ask/ChatInput';
import type { ChatMessage } from '../../contexts/AskSidebarContext';
import { agentApi, reportApi } from '../../services/reportApi';
import { chronologyApi } from '../../services/chronologyApi';
import { keyFindingsApi } from '../../services/keyFindingsApi';
import type { Template, TemplateSection } from '../../services/templateApi';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ReportPreferences {
  audience: string;
  focusAreas: string[];
  keyEvents: string[];
  scope: string;
  citationStyle: string;
  additionalGuidance: string;
  suggestedTitle: string;
}

interface ConversationalSetupViewProps {
  caseId: string;
  template: Template;
  sections: TemplateSection[];
  onPreferencesReady: (preferences: ReportPreferences, title: string) => void;
  onBack: () => void;
  /** Optional hint that adjusts the seed message focus (e.g. 'key-findings', 'chronology'). */
  focusHint?: string;
}

// ─── Preference extraction ──────────────────────────────────────────────────

/**
 * Regex that matches the sentinel block in all variants the LLM might produce:
 *   <<<PREFERENCES>>> ... <<</PREFERENCES>>>
 *   <<<PREFERENCES>>> ... <<<\/PREFERENCES>>>
 *   <<<PREFERENCES>>> ... <<<\\\/PREFERENCES>>>
 * Also handles markdown-escaped backticks wrapping the sentinels.
 */
const PREF_BLOCK_RE = /<<<PREFERENCES>>>([\s\S]*?)<<<\\?\/PREFERENCES>>>/;

/**
 * Fallback: find a JSON object containing "audience" anywhere in the text.
 * Covers cases where the LLM omits the sentinel markers entirely.
 */
const FALLBACK_JSON_RE = /\{[^{}]*"audience"\s*:\s*"[^"]*"[^{}]*\}/;

/**
 * Regex for quick-reply suggestions embedded by the AI.
 * Format: <<<QUICK_REPLIES>>>["option1", "option2"]<<<\/QUICK_REPLIES>>>
 */
const QUICK_REPLIES_RE = /<<<QUICK_REPLIES>>>([\s\S]*?)<<<\\?\/QUICK_REPLIES>>>/;

function parsePrefsJson(jsonStr: string): ReportPreferences | null {
  try {
    const parsed = JSON.parse(jsonStr);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      audience: parsed.audience ?? '',
      focusAreas: Array.isArray(parsed.focusAreas) ? parsed.focusAreas : [],
      keyEvents: Array.isArray(parsed.keyEvents) ? parsed.keyEvents : [],
      scope: parsed.scope ?? '',
      citationStyle: parsed.citationStyle ?? '',
      additionalGuidance: parsed.additionalGuidance ?? '',
      suggestedTitle: parsed.suggestedTitle ?? '',
    };
  } catch {
    return null;
  }
}

function extractPreferences(text: string): ReportPreferences | null {
  // Try sentinel-based extraction first
  const match = PREF_BLOCK_RE.exec(text);
  if (match) {
    const result = parsePrefsJson(match[1].trim());
    if (result) return result;
  }

  // Fallback: look for a bare JSON object with "audience" key
  const fallback = FALLBACK_JSON_RE.exec(text);
  if (fallback) {
    const result = parsePrefsJson(fallback[0]);
    if (result) return result;
  }

  return null;
}

function extractQuickReplies(text: string): QuickReplySet | null {
  const match = QUICK_REPLIES_RE.exec(text);
  if (!match) return null;
  try {
    const parsed = JSON.parse(match[1].trim());
    // Array format: ["Option A", "Option B"] — single-select
    if (Array.isArray(parsed) && parsed.length > 0 && parsed.every(o => typeof o === 'string')) {
      return { options: parsed, allowOther: true };
    }
    // Object format: { "options": [...], "multiSelect": true } — multi-select
    if (parsed && typeof parsed === 'object' && Array.isArray(parsed.options) && parsed.options.length > 0) {
      return { options: parsed.options, allowOther: true, multiSelect: !!parsed.multiSelect };
    }
  } catch { /* ignore parse errors */ }
  return null;
}

function stripPreferencesBlock(text: string): string {
  // Strip sentinel block (all variants)
  let cleaned = text.replace(/<<<PREFERENCES>>>[\s\S]*?<<<\\?\/PREFERENCES>>>/g, '');
  // Strip quick-reply sentinel blocks
  cleaned = cleaned.replace(/<<<QUICK_REPLIES>>>[\s\S]*?<<<\\?\/QUICK_REPLIES>>>/g, '');
  // Also strip bare JSON objects that look like preference blocks
  cleaned = cleaned.replace(/\{[^{}]*"audience"\s*:\s*"[^"]*"[^{}]*\}/g, '');
  return cleaned.trim();
}

// ─── Quick-reply detection ──────────────────────────────────────────────────

interface QuickReplySet {
  options: string[];
  allowOther: boolean;
  multiSelect?: boolean;
}

/** Detect what the AI is asking about and return contextual quick-reply buttons. */
function detectQuickReplies(lastAssistantMessage: string): QuickReplySet | null {
  const lower = lastAssistantMessage.toLowerCase();

  // Final "anything else" question — check FIRST, as these messages often
  // contain words like "highlight" or "particular" that would match other patterns
  if (
    lower.includes('anything else') || lower.includes('bear in mind') ||
    lower.includes('anything further') || lower.includes('missed anything')
  ) {
    return {
      options: ['No, that covers everything', 'Keep it concise — avoid unnecessary detail', 'Use formal CPR-compliant language throughout'],
      allowOther: true,
    };
  }

  // Audience question
  if (
    (lower.includes('audience') || lower.includes('who is') || lower.includes('prepared for') || lower.includes('intended for')) &&
    (lower.includes('court') || lower.includes('solicitor') || lower.includes('insurer') || lower.includes('party'))
  ) {
    return {
      options: ['Court', 'Instructing solicitor', 'Insurer', 'Joint instruction'],
      allowOther: true,
    };
  }

  // Citation style question
  if (
    lower.includes('citation') || lower.includes('referenc') || lower.includes('footnot') ||
    lower.includes('source document') || lower.includes('cited') || lower.includes('citing')
  ) {
    return {
      options: ['Numbered footnotes', 'Inline page references', 'Appendix bibliography', 'No citations'],
      allowOther: true,
    };
  }

  // Focus / clinical areas question
  if (
    lower.includes('clinical area') || lower.includes('focus on') || lower.includes('specialt') ||
    lower.includes('injuries') || lower.includes('conditions')
  ) {
    return {
      options: ['Neurology', 'Orthopaedics', 'Psychiatry', 'Pain medicine', 'General / all areas'],
      allowOther: true,
    };
  }

  // Key events question
  if (
    lower.includes('key event') || lower.includes('particular event') || lower.includes('particular date') ||
    lower.includes('emphasise') || lower.includes('turning point')
  ) {
    return {
      options: ['High-level overview only', 'Focus on the index event', 'Include all significant events'],
      allowOther: true,
    };
  }

  // Scope question
  if (
    lower.includes('scope') || lower.includes('full scope') || lower.includes('subset') ||
    lower.includes('briefly reference') || lower.includes('cover the full')
  ) {
    return {
      options: ['Full scope', 'Primary specialty only', 'Briefly reference other areas'],
      allowOther: true,
    };
  }

  return null;
}

// ─── Quick Reply Chips ──────────────────────────────────────────────────────

const QuickReplyChips: React.FC<{
  replies: QuickReplySet;
  onSelect: (text: string) => void;
  disabled: boolean;
}> = ({ replies, onSelect, disabled }) => {
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [otherText, setOtherText] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showOtherInput) inputRef.current?.focus();
  }, [showOtherInput]);

  const isMulti = !!replies.multiSelect;

  const toggleOption = (option: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(option)) next.delete(option);
      else next.add(option);
      return next;
    });
  };

  const handleConfirm = () => {
    if (selected.size > 0) {
      onSelect(Array.from(selected).join(', '));
    }
  };

  return (
    <div className="space-y-1.5">
      {isMulti && (
        <p className="text-[10px] text-medical-400 dark:text-medical-500 italic">
          Select all that apply
        </p>
      )}
      <div className="flex flex-col gap-1">
        {replies.options.map((option) => {
          const isSelected = selected.has(option);
          return (
            <button
              key={option}
              onClick={() => isMulti ? toggleOption(option) : onSelect(option)}
              disabled={disabled}
              data-testid="quick-reply-option"
              className={`w-full text-left px-2.5 py-2 text-xs leading-snug rounded-lg border transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                isMulti && isSelected
                  ? 'border-primary-400 dark:border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 ring-1 ring-primary-300 dark:ring-primary-600'
                  : 'border-medical-200 dark:border-medical-600 text-medical-700 dark:text-medical-200 bg-medical-50 dark:bg-medical-700/50 hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:border-primary-300 dark:hover:border-primary-600 hover:text-primary-700 dark:hover:text-primary-300'
              }`}
            >
              {isMulti && (
                <span className={`inline-flex items-center justify-center w-3.5 h-3.5 mr-2 rounded border text-[9px] flex-shrink-0 ${
                  isSelected
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : 'border-medical-300 dark:border-medical-500'
                }`}>
                  {isSelected ? '✓' : ''}
                </span>
              )}
              {option}
            </button>
          );
        })}
        {replies.allowOther && !showOtherInput && (
          <button
            onClick={() => setShowOtherInput(true)}
            disabled={disabled}
            className="w-full text-left px-2.5 py-2 text-xs rounded-lg border border-dashed border-medical-200 dark:border-medical-600 text-medical-500 dark:text-medical-400 hover:bg-medical-50 dark:hover:bg-medical-700/50 hover:border-medical-300 dark:hover:border-medical-500 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Other…
          </button>
        )}
      </div>

      {isMulti && selected.size > 0 && (
        <button
          onClick={handleConfirm}
          disabled={disabled}
          className="w-full px-2.5 py-2 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40 transition-colors"
        >
          Confirm {selected.size} selection{selected.size !== 1 ? 's' : ''}
        </button>
      )}

      {showOtherInput && (
        <div className="flex gap-1.5">
          <input
            ref={inputRef}
            type="text"
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && otherText.trim()) {
                if (isMulti) {
                  toggleOption(otherText.trim());
                  setOtherText('');
                } else {
                  onSelect(otherText.trim());
                  setShowOtherInput(false);
                  setOtherText('');
                }
              }
              if (e.key === 'Escape') {
                setShowOtherInput(false);
                setOtherText('');
              }
            }}
            placeholder={isMulti ? 'Add another option…' : 'Type your answer…'}
            className="flex-1 px-2.5 py-1.5 text-xs border border-medical-200 dark:border-medical-600 rounded-lg bg-white dark:bg-medical-700 text-medical-800 dark:text-medical-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <button
            onClick={() => {
              if (otherText.trim()) {
                if (isMulti) {
                  toggleOption(otherText.trim());
                  setOtherText('');
                } else {
                  onSelect(otherText.trim());
                  setShowOtherInput(false);
                  setOtherText('');
                }
              }
            }}
            disabled={!otherText.trim()}
            className="px-2.5 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40 transition-colors"
          >
            {isMulti ? 'Add' : 'Send'}
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Component ──────────────────────────────────────────────────────────────

export const ConversationalSetupView: React.FC<ConversationalSetupViewProps> = ({
  caseId,
  template,
  sections,
  onPreferencesReady,
  onBack,
  focusHint,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [preferencesReady, setPreferencesReady] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const conversationIdRef = useRef<string | null>(null);
  const exchangeCountRef = useRef(0);
  const preferencesEmittedRef = useRef(false);
  const hasInitialised = useRef(false);
  const lastQuickRepliesRef = useRef<QuickReplySet | null>(null);

  // Build the seeded instruction with live case context
  const buildSeedMessage = useCallback((caseContext: string): string => {
    const sectionList = sections.map(s => s.title).join(', ');

    // Focus-specific preamble and question guidance
    let focusPreamble = '';
    let focusQuestions = '';
    if (focusHint === 'key-findings') {
      focusPreamble = [
        `You are helping the user set up a "Key Findings Report" — a report focused specifically on the starred key findings already identified for this case. The template has these sections: ${sectionList}.`,
        '',
        'The report should synthesise and expand on the key findings from the case records. The user has already starred important findings during their review, and this report should centre on those.',
      ].join('\n');
      focusQuestions = [
        'Areas to cover (tailored to this case and its starred findings):',
        '- Who is the intended audience? (Court, instructing solicitor, insurer, etc.)',
        '- Looking at the key findings in this case, are there any the user wants to prioritise or exclude from this report? Reference the actual findings.',
        '- Should the report group findings by clinical area, chronology, or significance?',
        '- How should source documents be cited in the report? (e.g. numbered footnotes, inline page references like "[GP records, p.12]", appendix-style bibliography, or no citations)',
        '- FINAL QUESTION (always ask this last): "Is there anything else you\'d like me to bear in mind when drafting this report?"',
      ].join('\n');
    } else if (focusHint === 'chronology') {
      focusPreamble = [
        `You are helping the user set up a "Chronology Summary" — a structured chronological narrative of the medical records. The template has these sections: ${sectionList}.`,
        '',
        'The report should provide a clear, chronological account of the clinical events documented in the case records.',
      ].join('\n');
      focusQuestions = [
        'Areas to cover (tailored to this case):',
        '- Who is the intended audience? (Court, instructing solicitor, insurer, etc.)',
        '- Should the chronology cover the full timeline or focus on a specific period? Reference the actual date range from the records.',
        '- Are there particular clinical areas or events the user wants emphasised or excluded?',
        '- How should source documents be cited in the report? (e.g. numbered footnotes, inline page references like "[GP records, p.12]", appendix-style bibliography, or no citations)',
        '- FINAL QUESTION (always ask this last): "Is there anything else you\'d like me to bear in mind when drafting this report?"',
      ].join('\n');
    } else {
      focusPreamble = `You are helping the user set up a "${template.name}" medical-legal report. The template has these sections: ${sectionList}.`;
      focusQuestions = [
        'Areas to cover (tailored to this case):',
        '- Who is the intended audience? (Court, instructing solicitor, insurer, etc.)',
        '- Given the clinical areas in this case, which should the report focus on? Name the specific conditions/injuries found.',
        '- Are there particular key events the user wants to emphasise or exclude? Reference actual dates and events from the case.',
        '- How should source documents be cited in the report? (e.g. numbered footnotes, inline page references like "[GP records, p.12]", appendix-style bibliography, or no citations)',
        '- FINAL QUESTION (always ask this last): "Is there anything else you\'d like me to bear in mind when drafting this report?" — this gives the user a chance to add any preferences the wizard may have missed.',
      ].join('\n');
    }

    return [
      focusPreamble,
      '',
      '--- CASE CONTEXT (from the case records — use this to ask informed, case-specific questions) ---',
      caseContext,
      '--- END CASE CONTEXT ---',
      '',
      'Your job is to ask the user 3–5 focused questions (ONE question per message) to establish their preferences. Because you already have the case context above, your questions should be SPECIFIC to this case — reference actual injuries, events, dates, and clinical areas from the records. Do NOT ask generic template questions.',
      '',
      focusQuestions,
      '',
      'RULES:',
      '- Ask ONE question at a time. Be conversational and concise.',
      '- Reference specific details from the case context (patient injuries, dates, clinical areas) in your questions.',
      '- Do NOT use any tools — you cannot access documents directly during this setup conversation, but all case documents have been fully processed and their content is reflected in the case context above. The report generation stage that follows will have full access to every document. If the user asks you to check a document, explain that you are working from the processed case data shown above, and the full document text will be used during report generation.',
      '- ALWAYS ask the "anything else" question as your final question before emitting preferences.',
      '- After EVERY question, suggest 3–5 contextual quick-reply options relevant to THAT question and THIS case by appending a sentinel block.',
      '- For single-choice questions (e.g. audience, citation style), use a simple array:',
      '',
      '<<<QUICK_REPLIES>>>',
      '["Option A", "Option B", "Option C"]',
      '<<<\\/QUICK_REPLIES>>>',
      '',
      '- For questions where the user may want to select MULTIPLE answers (e.g. which clinical areas to cover, which injuries to include, which key events to highlight), use the object format with multiSelect:',
      '',
      '<<<QUICK_REPLIES>>>',
      '{ "options": ["Option A", "Option B", "Option C"], "multiSelect": true }',
      '<<<\\/QUICK_REPLIES>>>',
      '',
      '- Use multiSelect whenever the question naturally allows more than one answer. Think: "could the user reasonably want two or more of these?" If yes, use multiSelect.',
      '- The options MUST be specific to the question being asked and to the case records. For clinical area questions, list only the specialties present in this case. For event questions, list the actual key events. Do NOT use generic/hardcoded options.',
      '- After the user has answered all questions (including the final "anything else" question), write a brief one-sentence confirmation and IMMEDIATELY after it emit your gathered preferences as a JSON block wrapped in sentinel markers:',
      '',
      '<<<PREFERENCES>>>',
      '{ "audience": "...", "focusAreas": ["..."], "keyEvents": ["..."], "scope": "...", "citationStyle": "...", "additionalGuidance": "...", "suggestedTitle": "..." }',
      '<<<\\/PREFERENCES>>>',
      '',
      '- The suggestedTitle should be a concise, professional report title relevant to this case.',
      '- IMPORTANT: Do NOT write a summary list of the preferences as visible text. The sentinel block IS the output — the system handles the transition.',
      '- The one-sentence confirmation before the sentinel block is the ONLY visible text in your final message.',
      '- Start by greeting the user briefly (you may reference the case overview) and asking your first question.',
    ].join('\n');
  }, [template.name, sections, focusHint]);

  // Send a message to the agent and stream the response
  const sendMessage = useCallback((userMessage: string, isHidden: boolean = false) => {
    if (preferencesEmittedRef.current) return;

    // Add user message to chat (unless it's the hidden seed)
    if (!isHidden) {
      const userMsg: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: userMessage,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, userMsg]);
      exchangeCountRef.current += 1;
    }

    setIsLoading(true);

    // Create assistant placeholder
    const assistantId = `assistant-${Date.now()}`;
    const assistantMsg: ChatMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    };
    setMessages(prev => [...prev, assistantMsg]);

    let accumulated = '';

    agentApi.chat(
      {
        message: userMessage,
        caseId,
        conversationId: conversationIdRef.current,
      },
      {
        onThinking: () => {},
        onToolCall: () => {},
        onToolResult: () => {},
        onToken: (token: string) => {
          accumulated += token;
          const displayText = stripPreferencesBlock(accumulated);
          setMessages(prev =>
            prev.map(m => m.id === assistantId ? { ...m, content: displayText } : m),
          );
        },
        onDone: (event) => {
          if (event.conversationId) conversationIdRef.current = event.conversationId;

          // Extract AI-suggested quick replies before stripping sentinels
          lastQuickRepliesRef.current = extractQuickReplies(accumulated);

          const displayText = stripPreferencesBlock(accumulated);
          setMessages(prev =>
            prev.map(m =>
              m.id === assistantId ? { ...m, content: displayText, isStreaming: false } : m,
            ),
          );
          setIsLoading(false);

          // Check for preferences
          const prefs = extractPreferences(accumulated);
          if (prefs && !preferencesEmittedRef.current) {
            preferencesEmittedRef.current = true;
            setPreferencesReady(true); // state triggers re-render for transition UI
            setTimeout(() => {
              onPreferencesReady(prefs, prefs.suggestedTitle || template.name);
            }, 1500);
          }
        },
        onError: (err: Error) => {
          console.error('Conversational setup error:', err);
          setMessages(prev =>
            prev.map(m =>
              m.id === assistantId
                ? { ...m, content: 'Something went wrong. Please try again.', isStreaming: false }
                : m,
            ),
          );
          setIsLoading(false);
        },
      },
    );
  }, [caseId, onPreferencesReady, template.name]);

  // Fetch case context and kick off the conversation
  useEffect(() => {
    if (hasInitialised.current) return;
    hasInitialised.current = true;

    const init = async () => {
      // Fetch case data in parallel — all are optional, failures are fine
      const [chronology, keyFindings, caseData] = await Promise.all([
        chronologyApi.getChronologyByCaseId(caseId).catch(() => null),
        keyFindingsApi.getForCase(caseId).catch(() => []),
        reportApi.getAvailableCaseData(caseId).catch(() => null),
      ]);

      // Build a compact case context string
      const contextParts: string[] = [];

      // Patient + overview
      if (chronology?.patientName) {
        contextParts.push(`Patient: ${chronology.patientName}`);
      }
      if (chronology?.narrative?.overviewSummary) {
        contextParts.push(`Case overview: ${stripPiiMarkers(chronology.narrative.overviewSummary)}`);
      }

      // Documents
      const docs = caseData?.documents ?? [];
      if (docs.length > 0) {
        contextParts.push(`Documents (${docs.length}): ${docs.map(d => d.name).join(', ')}`);
      }

      // Instruction letter
      const instrLetter = caseData?.instructionLetter;
      if (instrLetter?.caseSummary) {
        contextParts.push(`Instruction letter summary: ${instrLetter.caseSummary}`);
      }
      if (instrLetter?.questionsToAnswer?.length) {
        contextParts.push(`Questions to address: ${instrLetter.questionsToAnswer.map(q => q.summary).join('; ')}`);
      }
      if (instrLetter?.parties) {
        const p = instrLetter.parties;
        const partyParts = [
          p.claimant && `Claimant: ${p.claimant}`,
          p.defendant && `Defendant: ${p.defendant}`,
          p.solicitor && `Solicitor: ${p.solicitor}`,
          p.expert && `Expert: ${p.expert}`,
        ].filter(Boolean);
        if (partyParts.length) contextParts.push(`Parties: ${partyParts.join(', ')}`);
      }

      // Key events (top 10 by significance)
      const events = chronology?.events ?? [];
      if (events.length > 0) {
        const majorEvents = events
          .filter((e: any) => e.significance === 'MAJOR' || !e.significance)
          .slice(0, 10);
        const eventSummaries = majorEvents.map((e: any) =>
          `${e.eventDate?.slice(0, 10) ?? 'Unknown date'}: ${e.headline || e.description?.slice(0, 100)}`,
        );
        contextParts.push(`Key events (${events.length} total, top ${majorEvents.length}):\n${eventSummaries.join('\n')}`);
      }

      // Key findings
      if (keyFindings && keyFindings.length > 0) {
        const findingSummaries = keyFindings.slice(0, 8).map((f: any) =>
          f.note || f.selectedText?.slice(0, 100) || f.eventHeadline || 'Finding',
        );
        contextParts.push(`Key findings (${keyFindings.length}):\n${findingSummaries.join('\n')}`);
      }

      const caseContext = contextParts.length > 0
        ? contextParts.join('\n\n')
        : 'No case data available yet — ask general questions about the user\'s preferences.';

      sendMessage(buildSeedMessage(caseContext), true);
    };

    init();
  }, [caseId, buildSeedMessage, sendMessage]);

  const handleSend = useCallback((text: string) => {
    sendMessage(text);
  }, [sendMessage]);

  // Build default preferences from conversation when the AI didn't emit structured data
  const buildDefaultPreferences = useCallback((): ReportPreferences => {
    // Collect all user messages as additional guidance
    const userMessages = messages
      .filter(m => m.role === 'user')
      .map(m => m.content)
      .join('. ');

    return {
      audience: '',
      focusAreas: [],
      keyEvents: [],
      scope: '',
      citationStyle: '',
      additionalGuidance: `User conversation context: ${userMessages}`,
      suggestedTitle: template.name,
    };
  }, [messages, template.name]);

  // Auto-trigger generation when the AI wraps up without emitting the sentinel.
  // This fires when the last assistant message looks like a conclusion and we have
  // enough exchanges, rather than showing a button the user might miss.
  const autoTriggerChecked = useRef(false);
  useEffect(() => {
    if (isLoading || preferencesReady || preferencesEmittedRef.current) return;
    if (exchangeCountRef.current < 2) return;

    // Find the last assistant message
    let looksLikeWrapUp = false;
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role === 'assistant' && msg.content && !msg.isStreaming) {
        const lower = msg.content.toLowerCase();
        looksLikeWrapUp = lower.includes('everything i need') ||
          lower.includes('starting your report') ||
          lower.includes('have enough') ||
          lower.includes('let me set up') ||
          lower.includes('ready to generate') ||
          lower.includes('i\'ll start') ||
          lower.includes('begin generating') ||
          lower.includes('i\'ll get started') ||
          lower.includes('let me get') ||
          lower.includes('i\'ll now') ||
          lower.includes('generating your');
        break;
      }
    }

    const shouldAutoTrigger = looksLikeWrapUp || exchangeCountRef.current >= 6;
    if (!shouldAutoTrigger || autoTriggerChecked.current) return;
    autoTriggerChecked.current = true;

    // Auto-generate after a short delay so the user sees the wrap-up message
    const timer = setTimeout(() => {
      if (preferencesEmittedRef.current) return;
      preferencesEmittedRef.current = true;
      setPreferencesReady(true);
      const prefs = buildDefaultPreferences();
      onPreferencesReady(prefs, template.name);
    }, 2000);

    return () => clearTimeout(timer);
  }, [messages, isLoading, preferencesReady, buildDefaultPreferences, onPreferencesReady, template.name]);

  const handleNudge = useCallback(() => {
    sendMessage(
      'I think we have enough information now. Please finalise the preferences and emit the <<<PREFERENCES>>> block so we can start generating.',
    );
  }, [sendMessage]);

  // Auto-scroll to bottom when quick replies appear
  useEffect(() => {
    if (!isLoading && scrollAreaRef.current) {
      requestAnimationFrame(() => {
        scrollAreaRef.current?.scrollTo({ top: scrollAreaRef.current.scrollHeight, behavior: 'smooth' });
      });
    }
  }, [isLoading, messages.length]);

  // Detect quick-reply options from the latest assistant message.
  // Prefer AI-suggested replies (case-specific), fall back to hardcoded keyword detection.
  const quickReplies = useMemo((): QuickReplySet | null => {
    if (isLoading || preferencesReady) return null;
    // Find the last assistant message (use raw accumulated text for sentinel parsing)
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role === 'assistant' && msg.content && !msg.isStreaming) {
        // The displayed content has sentinels stripped, so re-check isn't possible.
        // We store AI-suggested replies from the raw stream in a ref instead.
        return lastQuickRepliesRef.current ?? detectQuickReplies(msg.content);
      }
    }
    return null;
  }, [messages, isLoading, preferencesReady]);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="px-3 py-2 border-b border-medical-100 dark:border-medical-700 flex items-center gap-2 flex-shrink-0">
        <button
          onClick={onBack}
          className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
        >
          <ArrowLeft className="h-3 w-3" />
          Back
        </button>
        <span className="text-medical-300 dark:text-medical-600">|</span>
        <span className="text-xs text-medical-600 dark:text-medical-400 truncate">{template.name}</span>
      </div>

      {/* Chat thread + inline quick-reply chips */}
      <div className="flex-1 overflow-y-auto px-3 py-3 min-h-0" ref={scrollAreaRef}>
        <ChatThread messages={messages} isLoading={isLoading && messages.length === 0} variant="compact" />

        {/* Quick-reply chips — rendered inline, directly below the AI question */}
        {!preferencesReady && quickReplies && (
          <div className="mt-2 ml-7">
            <QuickReplyChips
              replies={quickReplies}
              onSelect={handleSend}
              disabled={isLoading}
            />
          </div>
        )}

      </div>

      {/* Transition indicator when preferences are ready */}
      {preferencesReady && (
        <div className="px-3 py-3 bg-emerald-50 dark:bg-emerald-900/10 border-t border-emerald-200 dark:border-emerald-800/30 flex items-center gap-2.5 flex-shrink-0">
          <div className="p-1.5 rounded-md bg-emerald-100 dark:bg-emerald-900/30">
            <Sparkles className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <p className="text-xs font-medium text-emerald-700 dark:text-emerald-300">Setting up your report…</p>
            <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-0.5">Creating skeleton and starting generation</p>
          </div>
          <Loader className="h-3.5 w-3.5 animate-spin text-emerald-500 ml-auto" />
        </div>
      )}

      {/* Chat input — hidden when preferences are ready */}
      {!preferencesReady && (
        <div className="px-3 py-2 border-t border-medical-100 dark:border-medical-700 flex-shrink-0">
          <ChatInput
            onSend={handleSend}
            isLoading={isLoading}
            placeholder="Or type your answer…"
          />
        </div>
      )}
    </div>
  );
};
